import React, { useState } from "react";
import { View, Text, TextInput, StyleSheet, TouchableOpacity } from "react-native";
import { Ionicons } from "@expo/vector-icons";
import { useApi } from "../context/ApiContext";

const ProjectTypeCard = ({ type, icon, label, selected, onSelect, disabled }) => (
	<TouchableOpacity style={[styles.projectTypeCard, selected && styles.selectedProjectTypeCard, disabled && styles.disabledProjectTypeCard]} onPress={() => !disabled && onSelect(type)} disabled={disabled}>
		<Ionicons name={icon} size={24} color={disabled ? "#444" : selected ? "#fff" : "#666"} />
		<Text style={[styles.projectTypeLabel, selected && styles.selectedProjectTypeLabel, disabled && styles.disabledProjectTypeLabel]}>{label}</Text>
		{disabled && <Text style={styles.comingSoonLabel}>Coming Soon</Text>}
	</TouchableOpacity>
);

const CreateProjectModal = ({ onClose, onCreateProject }) => {
	const [selectedType, setSelectedType] = useState("mobile");
	const [projectName, setProjectName] = useState("");
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState(null);

	const { api } = useApi();

	const handleCreateProject = async () => {
		try {
			setIsLoading(true);
			setError(null);

			const projectData = {
				name: projectName,
				type: selectedType,
				bundleId: `app.executable.${projectName.toLowerCase().replace(/[^a-z0-9]/g, "")}`,
			};

			const project = await api("/app", {
				method: "POST",
				body: projectData,
			});

			onCreateProject(projectData);
			onClose();
		} catch (err) {
			console.error("Error creating project:", err);
			const errorResponse = err.response?.data;
			setError(errorResponse?.errors || [err.message || "Failed to create project"]);
		} finally {
			setIsLoading(false);
		}
	};

	return (
		<View style={styles.modalOverlay}>
			<View style={styles.modalContent}>
				<Text style={styles.modalTitle}>Create New Project</Text>

				{error && (
					<View style={styles.errorContainer}>
						{Array.isArray(error) ? (
							error.map((err, index) => (
								<Text key={index} style={styles.errorText}>
									{err}
								</Text>
							))
						) : (
							<Text style={styles.errorText}>{error}</Text>
						)}
					</View>
				)}

				<Text style={styles.sectionLabel}>Select project</Text>
				<View style={styles.projectTypes}>
					<ProjectTypeCard type="mobile" icon="phone-portrait-outline" label="Mobile App" selected={selectedType === "mobile"} onSelect={setSelectedType} />
					<ProjectTypeCard type="web" icon="globe-outline" label="Website" selected={selectedType === "web"} onSelect={setSelectedType} disabled={true} />
				</View>

				<View style={styles.inputGroup}>
					<Text style={styles.inputLabel}>Project Name</Text>
					<TextInput style={styles.input} value={projectName} onChangeText={setProjectName} placeholder="Enter project name" placeholderTextColor="#666" editable={!isLoading} />
				</View>

				<View style={styles.actions}>
					<TouchableOpacity style={styles.cancelButton} onPress={onClose} disabled={isLoading}>
						<Text style={[styles.cancelButtonText, isLoading && styles.disabledText]}>Cancel</Text>
					</TouchableOpacity>
					<TouchableOpacity style={[styles.createButton, (!projectName || isLoading) && styles.createButtonDisabled]} onPress={handleCreateProject} disabled={!projectName || isLoading}>
						<Text style={styles.createButtonText}>{isLoading ? "Creating..." : "Create New Project"}</Text>
					</TouchableOpacity>
				</View>
			</View>
		</View>
	);
};

const styles = StyleSheet.create({
	modalOverlay: {
		position: "absolute",
		top: 0,
		left: 0,
		right: 0,
		bottom: 0,
		backgroundColor: "rgba(0, 0, 0, 0.75)",
		justifyContent: "center",
		alignItems: "center",
		zIndex: 1000,
	},
	modalContent: {
		backgroundColor: "#1A1A1A",
		borderRadius: 12,
		padding: 24,
		width: 480,
	},
	modalTitle: {
		fontSize: 18,
		fontWeight: "600",
		color: "#fff",
		marginBottom: 24,
	},
	sectionLabel: {
		fontSize: 14,
		color: "#666",
		marginBottom: 12,
	},
	projectTypes: {
		flexDirection: "row",
		gap: 12,
		marginBottom: 24,
	},
	projectTypeCard: {
		flex: 1,
		backgroundColor: "#2A2A2A",
		borderRadius: 8,
		padding: 16,
		alignItems: "center",
		gap: 8,
		borderWidth: 1,
		borderColor: "transparent",
	},
	selectedProjectTypeCard: {
		borderColor: "#0A84FF",
		backgroundColor: "#0A84FF22",
	},
	projectTypeLabel: {
		fontSize: 14,
		color: "#666",
	},
	selectedProjectTypeLabel: {
		color: "#fff",
	},
	inputGroup: {
		marginBottom: 16,
	},
	inputLabel: {
		fontSize: 14,
		color: "#666",
		marginBottom: 8,
	},
	input: {
		backgroundColor: "#2A2A2A",
		borderRadius: 8,
		padding: 12,
		color: "#fff",
		fontSize: 14,
	},
	actions: {
		flexDirection: "row",
		justifyContent: "flex-end",
		gap: 12,
		marginTop: 24,
	},
	cancelButton: {
		padding: 12,
	},
	cancelButtonText: {
		color: "#666",
		fontSize: 14,
	},
	createButton: {
		backgroundColor: "#0A84FF",
		borderRadius: 8,
		padding: 12,
		paddingHorizontal: 20,
	},
	createButtonDisabled: {
		opacity: 0.5,
	},
	createButtonText: {
		color: "#fff",
		fontSize: 14,
		fontWeight: "600",
	},
	disabledProjectTypeCard: {
		opacity: 0.5,
		cursor: "not-allowed",
	},
	disabledProjectTypeLabel: {
		color: "#444",
	},
	comingSoonLabel: {
		fontSize: 12,
		color: "#444",
		marginTop: 4,
	},
	errorContainer: {
		backgroundColor: "#FF000022",
		padding: 12,
		borderRadius: 8,
		marginBottom: 16,
	},
	errorText: {
		color: "#FF0000",
		fontSize: 14,
	},
	disabledText: {
		opacity: 0.5,
	},
});

export default CreateProjectModal;
